/**
 * SaveListPanel — "Who do I save today?" answered in 3 seconds.
 *
 * THE INVERSION: the plan-to-approve that replaces the analysis-to-read.
 * At most five ranked cards (rankSaveList — churn probability × ARR at
 * stake), each carrying ONE recommended play with the drafted artifact
 * behind [Run save play]:
 *
 *   name · health dial · $ at risk · ~runway days · one-line why · play
 *
 * [Run save play] expands the email composed STRICTLY from real record
 * data (composeSavePlay); [Edit] adjusts it inline; [Approve & send]
 * queues the SAME governed `send_customer_update` proposal the weekly-
 * update pipeline uses into the approvals inbox — this component NEVER
 * sends anything itself. [Why?] folds open the churn dimensions, health
 * factors, and system findings for THAT account (progressive disclosure:
 * the rich math stays, one click away).
 *
 * Honest states: fewer than 5 at-risk shows fewer with "the rest are
 * stable"; no email on file shows "needs email" instead of a draft; a
 * play whose essentials are missing shows "no draft" instead of filler.
 */

import { useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  LifeBuoy,
  Pencil,
  Play,
  Send,
} from 'lucide-react';
import { GlassPanel } from './GlassPanel';
import { C } from '../lib/tokens';
import { queueApproval } from '../lib/agent/approvals';
import { buildSendCustomerUpdateAction } from '../lib/agent/weeklyUpdateAgent';
import { RISK_DIMENSION_LABELS } from '../lib/crm/churnRisk';
import {
  composeSavePlay,
  savePlayHtml,
  SAVE_LIST_CAP,
  type SaveListEntry,
} from '../lib/cs/savePlays';
import type { Deliverable } from '../lib/db/types';

function usd(n: number): string {
  return `$${n.toLocaleString('en-US')}`;
}

const BAND_COLOR: Record<SaveListEntry['health']['band'], string> = {
  'at-risk': C.magenta,
  watch: C.orange,
  healthy: C.green,
};

/** Compact health dial — the existing score, never re-derived. */
function SaveDial({ score, color }: { score: number; color: string }) {
  const size = 44;
  const stroke = 4;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const frac = Math.max(0, Math.min(100, score)) / 100;
  return (
    <svg
      role="img"
      aria-label={`Health ${score} of 100`}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ flex: '0 0 auto', display: 'block' }}
    >
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth={stroke} />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeDasharray={`${(frac * circ).toFixed(1)} ${circ.toFixed(1)}`}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text
        x="50%"
        y="50%"
        dominantBaseline="central"
        textAnchor="middle"
        fill={color}
        fontSize={size * 0.32}
        fontWeight={700}
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {score}
      </text>
    </svg>
  );
}

interface CardProps {
  entry: SaveListEntry;
  rank: number;
  canMutate: boolean;
  deliverables: Deliverable[];
  now?: number;
}

function SaveCard({ entry, rank, canMutate, deliverables, now }: CardProps) {
  const [draftOpen, setDraftOpen] = useState(false);
  const [whyOpen, setWhyOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [queuedNote, setQueuedNote] = useState<string | null>(null);

  const composed = useMemo(
    () =>
      composeSavePlay(entry.person, entry.play, {
        deliverables,
        ...(now !== undefined ? { now } : {}),
      }),
    [entry.person, entry.play, deliverables, now],
  );

  // Inline-editable copy of the draft — html regenerates from the edited text.
  const [subject, setSubject] = useState<string | null>(null);
  const [body, setBody] = useState<string | null>(null);
  const effectiveSubject = subject ?? composed?.subject ?? '';
  const effectiveBody = body ?? composed?.text ?? '';

  async function onApprove() {
    if (!composed || !entry.recipient || busy) return;
    setBusy(true);
    try {
      const edited = subject !== null || body !== null;
      const artifact = edited
        ? { subject: effectiveSubject, text: effectiveBody, html: savePlayHtml(effectiveBody) }
        : composed;
      await queueApproval(
        buildSendCustomerUpdateAction(entry.person, artifact, entry.recipient.email),
        'chat',
        `Save play (${entry.playLabel}) to ${entry.recipient.email} — drafted from this account's real record data.`,
        {
          rationale: entry.why,
          evidence: [
            `Churn ${entry.churn.churnProbability}% (confidence ${Math.round(entry.churn.confidence * 100)}%)`,
            `~${entry.runwayDays}d runway`,
            ...(entry.hasArr ? [`${usd(entry.arrAtStake)} ARR at stake`] : []),
          ],
        },
      );
      setQueuedNote('Queued for approval — nothing sends until you approve it in the inbox.');
    } finally {
      setBusy(false);
    }
  }

  const dimensions = entry.churn.dimensions.filter((d) => !d.missing);
  const negativeFactors = entry.health.factors.filter((f) => f.points < 0);

  return (
    <li
      className="glass-row"
      style={{ display: 'grid', gap: 10, padding: '12px 14px' }}
      aria-label={`Save #${rank}: ${entry.person.name}`}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <SaveDial score={entry.health.score} color={BAND_COLOR[entry.health.band]} />
        <div style={{ flex: '1 1 200px', minWidth: 0 }}>
          <span className="t-card-title" style={{ display: 'block' }}>
            {rank}. {entry.person.name}
          </span>
          <span className="t-meta" style={{ display: 'block', marginTop: 2 }}>
            {entry.why}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span
            className="glass-pill glass-pill--magenta"
            title={
              entry.hasArr
                ? `Churn ${entry.churn.churnProbability}% × ${usd(entry.arrAtStake)} ARR`
                : 'No ARR on record — nothing is invented'
            }
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {entry.hasArr ? `${usd(entry.arrAtStake)} at risk` : 'no ARR on record'}
          </span>
          <span
            className="glass-pill"
            title="Days of runway before the risk becomes irreversible (churn engine's own lead time)"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            ~{entry.runwayDays}d runway
          </span>
          <span className="glass-pill glass-pill--blue" title="The one recommended play, chosen from the top churn contributor">
            {entry.playLabel}
          </span>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        {entry.recipient === null ? (
          <span
            className="t-meta"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: C.orange }}
          >
            <AlertTriangle size={12} /> Needs email — no contact email on file. Add a POC email to
            draft this play.
          </span>
        ) : composed === null ? (
          <span className="t-meta" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <AlertTriangle size={12} /> No draft — the record is missing the real data this play
            needs.
          </span>
        ) : (
          <button
            type="button"
            className="glass-btn glass-btn--primary"
            onClick={() => setDraftOpen((v) => !v)}
            title="Expand the drafted email — composed only from this account's real record data"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11 }}
          >
            <Play size={12} /> {draftOpen ? 'Hide save play' : 'Run save play'}
          </button>
        )}
        <button
          type="button"
          className="glass-btn"
          aria-expanded={whyOpen}
          onClick={() => setWhyOpen((v) => !v)}
          title="Fold open the churn dimensions, health factors, and findings behind this card"
          style={{ fontSize: 11 }}
        >
          Why?
        </button>
      </div>

      {draftOpen && composed && entry.recipient && (
        <div
          style={{
            padding: '10px 12px',
            borderRadius: 10,
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
            display: 'grid',
            gap: 8,
          }}
        >
          <span className="t-meta">
            To {entry.recipient.name} &lt;{entry.recipient.email}&gt;
          </span>
          {editing ? (
            <>
              <input
                aria-label="Edit subject"
                className="glass-input"
                value={effectiveSubject}
                onChange={(e) => setSubject(e.target.value)}
                style={{ fontSize: 12 }}
              />
              <textarea
                aria-label="Edit draft"
                className="glass-input"
                value={effectiveBody}
                onChange={(e) => setBody(e.target.value)}
                rows={10}
                style={{ fontSize: 12, fontFamily: 'inherit', resize: 'vertical' }}
              />
            </>
          ) : (
            <>
              <span className="t-card-title" style={{ fontSize: 12 }}>
                {effectiveSubject}
              </span>
              <pre
                className="t-meta"
                style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}
              >
                {effectiveBody}
              </pre>
            </>
          )}
          {queuedNote ? (
            <span
              className="t-meta"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: C.green }}
            >
              <CheckCircle2 size={12} /> {queuedNote}
            </span>
          ) : (
            canMutate && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <button
                  type="button"
                  className="glass-btn glass-btn--primary"
                  onClick={() => void onApprove()}
                  disabled={busy}
                  title={`Queue this exact email to ${entry.recipient.email} into the approvals inbox — nothing sends without approval.`}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11 }}
                >
                  <Send size={12} /> {busy ? 'Queueing…' : 'Approve & send'}
                </button>
                <button
                  type="button"
                  className="glass-btn"
                  onClick={() => setEditing((v) => !v)}
                  title="Edit the subject and body inline before queueing"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11 }}
                >
                  <Pencil size={12} /> {editing ? 'Done editing' : 'Edit'}
                </button>
              </div>
            )
          )}
        </div>
      )}

      {whyOpen && (
        <div
          style={{
            padding: '10px 12px',
            borderRadius: 10,
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
            display: 'grid',
            gap: 6,
          }}
        >
          <span className="t-meta">
            Churn {entry.churn.churnProbability}% · confidence{' '}
            {Math.round(entry.churn.confidence * 100)}% · ~{entry.runwayDays}d runway
          </span>
          {dimensions.map((d) => (
            <span
              key={d.key}
              style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 11 }}
            >
              <span className="t-meta">
                {RISK_DIMENSION_LABELS[d.key]}
                {d.detail ? ` — ${d.detail}` : ''}
              </span>
              <span
                className="t-meta"
                style={{ fontVariantNumeric: 'tabular-nums', flex: '0 0 auto' }}
                title={`Risk ${d.score}/100 × weight ${d.weight} → contribution ${d.contribution}`}
              >
                +{d.contribution}
              </span>
            </span>
          ))}
          {negativeFactors.length > 0 && (
            <>
              <span className="t-eyebrow" style={{ marginTop: 4 }}>
                Health factors
              </span>
              {negativeFactors.map((f) => (
                <span key={f.label} className="t-meta" style={{ fontSize: 11 }}>
                  {f.label} ({f.points} pts)
                </span>
              ))}
            </>
          )}
          {entry.findings.length > 0 && (
            <>
              <span className="t-eyebrow" style={{ marginTop: 4 }}>
                Findings
              </span>
              {entry.findings.map((f) => (
                <span key={f.id} className="t-meta" style={{ fontSize: 11 }}>
                  {f.title}
                  {f.detail ? ` — ${f.detail}` : ''}
                </span>
              ))}
            </>
          )}
        </div>
      )}
    </li>
  );
}

export interface SaveListPanelProps {
  entries: SaveListEntry[];
  /** Live customers scored in total — powers the honest "rest are stable" line. */
  totalCustomers: number;
  canMutate: boolean;
  /** First-class deliverables — the unblock play composes from these. */
  deliverables: Deliverable[];
  /** Injectable clock for deterministic tests. */
  now?: number;
}

export function SaveListPanel({
  entries,
  totalCustomers,
  canMutate,
  deliverables,
  now,
}: SaveListPanelProps) {
  const stable = totalCustomers - entries.length;
  return (
    <GlassPanel variant="default" style={{ padding: '18px 20px' }}>
      <div className="dash-section-head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            aria-hidden="true"
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              display: 'inline-grid',
              placeItems: 'center',
              background: 'linear-gradient(135deg, rgba(255,0,255,0.22), rgba(187,65,152,0.12))',
              border: '1px solid rgba(255,255,255,0.14)',
              color: C.magenta,
            }}
          >
            <LifeBuoy size={17} strokeWidth={2} />
          </span>
          <div>
            <span className="t-eyebrow">Save list</span>
            <h2 className="t-section-title" style={{ margin: '4px 0 0' }}>
              Who do I save today?
            </h2>
          </div>
        </div>
      </div>

      {entries.length === 0 ? (
        <div className="crm-empty" style={{ padding: '20px 0' }}>
          <CheckCircle2 size={20} strokeWidth={1.75} />
          <span>
            No accounts need saving today —{' '}
            {totalCustomers > 0 ? 'the book is stable.' : 'no live customers to score yet.'}
          </span>
        </div>
      ) : (
        <>
          <p className="t-meta" style={{ margin: '0 0 10px' }}>
            {entries.length < SAVE_LIST_CAP && stable > 0
              ? `Only ${entries.length} account${entries.length === 1 ? ' needs' : 's need'} saving — the rest are stable.`
              : `Top ${entries.length} by churn probability × ARR at stake — one recommended play each, approval-gated.`}
          </p>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 10 }}>
            {entries.map((entry, i) => (
              <SaveCard
                key={entry.person.id}
                entry={entry}
                rank={i + 1}
                canMutate={canMutate}
                deliverables={deliverables}
                {...(now !== undefined ? { now } : {})}
              />
            ))}
          </ul>
        </>
      )}
    </GlassPanel>
  );
}
